import { Loader2, Lock, Paperclip, Send, Upload } from 'lucide-react';
import { useId, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { StatusPill } from '@/components/panels/SectionCard';
import { formatBytes } from '@/lib/metrics-api';
import { downloadAuthenticatedFile } from '@/lib/download';
import type { TicketAttachment, TicketMessage } from '@/lib/support-api';
import { relativeTime } from '@/modules/monitoring/format';

/**
 * The conversation itself, shared by the customer view and the staff queue.
 *
 * ## One component, because two would diverge
 *
 * Staff and customers read the same thread. Building it twice would mean the staff copy
 * eventually renders an internal note differently from how the customer copy would have — and the
 * only reason the customer copy never shows one is that the API cannot send it. Sharing the
 * component keeps that guarantee where it belongs: in the query, not in the markup.
 *
 * ## Internal notes are made loud on purpose
 *
 * A note that looks like a reply is a note somebody will write thinking it is private and phrase
 * accordingly, or write thinking it is public and confuse the customer. Amber background, a lock,
 * and the words "Internal note — the customer cannot see this" on every single one. There is no
 * subtle version of this that is safe.
 */
export function TicketThread({
  messages,
  attachments,
  attachmentPath,
}: {
  messages: TicketMessage[];
  attachments: TicketAttachment[];
  attachmentPath: (attachmentId: string) => string;
}) {
  return (
    <div className="space-y-4">
      {messages.map((message) => (
        <MessageBubble key={message.id} message={message} />
      ))}

      {attachments.length > 0 && (
        <div className="rounded-xl border border-dashed p-4">
          <p className="mb-3 flex items-center gap-2 text-sm font-medium">
            <Paperclip className="size-4" />
            Attachments
          </p>
          <ul className="space-y-2">
            {attachments.map((attachment) => (
              <li key={attachment.id} className="flex items-center justify-between gap-3 text-sm">
                <span className="min-w-0 flex-1 truncate">{attachment.filename}</span>
                <span className="shrink-0 text-xs text-muted-foreground">
                  {formatBytes(attachment.byte_size)}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    void downloadAuthenticatedFile(
                      attachmentPath(attachment.id),
                      attachment.filename,
                    )
                  }
                >
                  Download
                </Button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function MessageBubble({ message }: { message: TicketMessage }) {
  if (message.is_internal) {
    return (
      <div className="rounded-xl border border-amber-500/40 bg-amber-500/10 p-4">
        <div className="mb-2 flex flex-wrap items-center gap-2">
          <Lock className="size-3.5 text-amber-500" />
          <span className="text-xs font-semibold uppercase tracking-wide text-amber-500">
            Internal note. The customer cannot see this
          </span>
        </div>
        <p className="mb-1 text-sm font-medium">{message.author_name}</p>
        <p className="max-w-4xl whitespace-pre-wrap break-words text-sm">{message.body}</p>
        <p className="mt-2 text-xs text-muted-foreground">{relativeTime(message.created_at)}</p>
      </div>
    );
  }

  const fromStaff = message.author_role === 'staff';

  return (
    <div
      className={`rounded-xl border p-4 ${fromStaff ? 'border-primary/30 bg-primary/5' : 'bg-muted/20'}`}
    >
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm font-medium">
          {message.author_name}
          {fromStaff && (
            <span className="ml-2 rounded-full bg-primary/10 px-2 py-0.5 text-xs font-semibold text-primary">
              Support
            </span>
          )}
        </p>
        <p className="text-xs text-muted-foreground">{relativeTime(message.created_at)}</p>
      </div>
      {/* `whitespace-pre-wrap` on plain text, never dangerouslySetInnerHTML. A support thread is
          the single most attractive place in the product to inject markup: one side writes it and
          the other side — often staff, holding a privileged session — opens it. */}
      <p className="max-w-4xl whitespace-pre-wrap break-words text-sm">{message.body}</p>
    </div>
  );
}

/**
 * The reply box.
 *
 * `internal` is only ever passed by the staff view. The customer view does not render the toggle
 * and does not pass the prop, so there is no state in which a customer's reply could carry it.
 */
export function ReplyBox({
  onSend,
  onAttach,
  allowInternal = false,
  disabled = false,
  hint,
}: {
  onSend: (body: string, internal: boolean) => Promise<void>;
  onAttach?: ((file: File) => Promise<void>) | undefined;
  allowInternal?: boolean;
  disabled?: boolean;
  hint?: string | undefined;
}) {
  const [body, setBody] = useState('');
  const [internal, setInternal] = useState(false);
  const bodyId = useId();
  const [busy, setBusy] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);

  async function send() {
    if (body.trim() === '' || busy) {
      return;
    }

    setBusy(true);
    try {
      await onSend(body, internal);
      setBody('');
      setInternal(false);
    } finally {
      setBusy(false);
    }
  }

  async function attach(file: File) {
    if (onAttach === undefined) {
      return;
    }

    setBusy(true);
    try {
      await onAttach(file);
    } finally {
      setBusy(false);
      // Cleared so selecting the same file twice fires a change event the second time.
      if (fileInput.current !== null) {
        fileInput.current.value = '';
      }
    }
  }

  return (
    <div
      className={`rounded-xl border p-4 transition-colors ${internal ? 'border-amber-500/40 bg-amber-500/5' : ''}`}
    >
      <label htmlFor={bodyId} className="mb-2 block text-sm font-medium">
        {internal ? 'Internal note' : 'Reply'}
      </label>
      <textarea
        id={bodyId}
        value={body}
        onChange={(e) => setBody(e.target.value)}
        disabled={disabled || busy}
        rows={5}
        className="w-full resize-y rounded-md border bg-background p-3 text-sm outline-none focus:border-primary/50"
      />

      {allowInternal && (
        <label className="mt-3 flex cursor-pointer items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={internal}
            onChange={(e) => setInternal(e.target.checked)}
            className="size-4 accent-amber-500"
          />
          <Lock className="size-3.5 text-amber-500" />
          Internal note. The customer will not see this
        </label>
      )}

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <Button onClick={() => void send()} disabled={disabled || busy || body.trim() === ''}>
          {busy ? <Loader2 className="mr-2 size-4 animate-spin" /> : <Send className="mr-2 size-4" />}
          {internal ? 'Add note' : 'Send reply'}
        </Button>

        {onAttach !== undefined && (
          <>
            <input
              ref={fileInput}
              type="file"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file !== undefined) {
                  void attach(file);
                }
              }}
            />
            <Button
              variant="outline"
              onClick={() => fileInput.current?.click()}
              disabled={disabled || busy}
            >
              <Upload className="mr-2 size-4" />
              Attach a file
            </Button>
          </>
        )}

        {hint !== undefined && <p className="text-xs text-muted-foreground">{hint}</p>}
      </div>
    </div>
  );
}

/** The status chip, identical in both views. */
export function TicketStatusPill({ label, tone }: { label: string; tone: 'success' | 'warning' | 'danger' | 'muted' }) {
  return <StatusPill label={label} tone={tone} />;
}
